import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CallableNames, DbService } from '../../db.service';
import { ApiProvider, ApiType, IApi, IApiContent } from 'src/app/shared/models/api/api.model';
import { Currency } from 'src/app/shared/enums/price/currencies.enum';
import { ICalendar } from 'src/app/shared/models/calendars/calendar.model';

interface IApiCreateData {
  key: string;
  content: IApiContent;
  provider: ApiProvider;
  type: ApiType;
  organizationId: number;
  ok: boolean;
  active: boolean;
  test: boolean;
}

interface IApiUpdateData extends IApiCreateData {
  apiId: number;
}

interface ICallablesApiIbanCheckData {
  orgId: number;
  iban: string;
  test?: boolean;
}

type IApiIbanCheckResultData = {
  ok: true;
  data: {
    bankName: string;
    bankAccountNumber: string;
    bic: string;
    currencies: Currency[];
    countryCode: string;
  };
} | {
  ok: false;
  data: null;
};

interface ICallablesApiGoCardlessConnectData {
  orgId: number;
  countryCode: string;
  apiId: number | null;
  bic: string;
  iban: string;
  bankName: string;
  bankAccountNumber: string;
  currency: Currency;
  accountOwner: string;
}

interface IApiGoCardlessConnectResult {
  link: string;
  api: IApi;
}

export enum CalendarFor {
  LOCATION = 'LOCATION',
  LECTURER = 'LECTURER'
}

interface ICallablesApiGoogleCalendarGetAuthUrlData {
  orgId: number;
  for: CalendarFor;
  title: string;
  lecturerId?: number;
}

interface ICallablesApiGoogleCalendarExchangeCodeData {
  code: string;
  state: string;
}

interface ICallablesApiGoogleCalendarGetConnectionsData {
  orgId: number;
  lecturerUserId?: number;
}

interface ICallablesApiGoogleCalendarGetConnectionCalendarsData {
  connectionId: number;
}

export interface ICalendarFE {
  id?: number;
  title: string;
  description?: string;
  ownerEmail?: string;
  externalId: string;
}

interface ICallablesApiGoogleCalendarConnectData {
  connectionId: number;
  calendarId: string;
  locationId?: number;
  lecturerUserId?: number;
}

interface ICallablesApiGoogleCalendarGetConnectedCalendarsData {
  orgId: number;
  lecturerUserId?: number;
  locationId?: number;
}

interface ICallablesApiGoogleCalendarDisconnectData {
  orgId: number;
  calendarId: number;
  locationId?: number;
  lecturerUserId?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ApisService {

  constructor(
    private dbService: DbService,
  ) { }

  public create(data: IApiCreateData): Observable<IApi> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesApiCreate, data });
    return this.dbService.handleObs(obs);
  }

  public update(data: IApiUpdateData): Observable<IApi> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesApiUpdate, data });
    return this.dbService.handleObs(obs);
  }

  public checkIban(data: ICallablesApiIbanCheckData): Observable<IApiIbanCheckResultData> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesApiIbanCheck, data });
    return this.dbService.handleObs(obs, { allSilent: true });
  }

  public test(data: { api: IApi | null; apiId: number | null; }): Observable<boolean> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesApiTest, data });
    return this.dbService.handleObs(obs);
  }

  public delete(data: { apiId: number; }): Observable<boolean> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesApiDelete, data });
    return this.dbService.handleObs(obs);
  }

  public goCardlessConnect(data: ICallablesApiGoCardlessConnectData): Observable<IApiGoCardlessConnectResult> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesApiGoCardlessConnect, data });
    return this.dbService.handleObs(obs);
  }

  public goCardlessDisconnect(data: { apiId: number; }): Observable<IApi> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesApiGoCardlessDisconnect, data });
    return this.dbService.handleObs(obs);
  }

  public goCardlessReconnect(data: { apiId: number; }): Observable<IApiGoCardlessConnectResult> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesApiGoCardlessReconnect, data });
    return this.dbService.handleObs(obs);
  }

  public goCardlessActivate(data: { referenceId: string; }): Observable<IApi> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesApiGoCardlessActivate, data });
    return this.dbService.handleObs(obs);
  }

  public getGoogleCalendarAuthUrl(data: ICallablesApiGoogleCalendarGetAuthUrlData): Observable<string> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesApiGoogleCalendarGetAuthUrl, data });
    return this.dbService.handleObs(obs, { succSilent: true });
  }

  public exchangeGoogleCalendarCode(data: ICallablesApiGoogleCalendarExchangeCodeData): Observable<string> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesApiGoogleCalendarExchangeCode, data });
    return this.dbService.handleObs(obs);
  }

  public getGoogleCalendarConnections(data: ICallablesApiGoogleCalendarGetConnectionsData): Observable<IApi[]> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesApiGoogleCalendarGetConnections, data });
    return this.dbService.handleObs(obs, { succSilent: true });
  }

  public getGoogleConnectionCalendars(data: ICallablesApiGoogleCalendarGetConnectionCalendarsData): Observable<ICalendarFE[]> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesApiGoogleCalendarGetConnectionCalendars, data });
    return this.dbService.handleObs(obs, { succSilent: true });
  }

  public connectGoogleCalendar(data: ICallablesApiGoogleCalendarConnectData): Observable<ICalendar> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesApiGoogleCalendarConnect, data });
    return this.dbService.handleObs(obs, { succSilent: true });
  }

  public disconnectGoogleCalendar(data: ICallablesApiGoogleCalendarDisconnectData): Observable<ICalendar> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesApiGoogleCalendarDisconnect, data });
    return this.dbService.handleObs(obs);
  }

  public getConnectedCalendars(data: ICallablesApiGoogleCalendarGetConnectedCalendarsData): Observable<ICalendarFE[]> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesApiGoogleCalendarGetConnectedCalendars, data });
    return this.dbService.handleObs(obs, { succSilent: true });
  }
}
